import { useCallback, useEffect, useId, useState } from 'react';
import { Check, RotateCcw, UserCheck, X } from 'lucide-react';
import { useLocale, useT } from '../../i18n';
import { getEloLevel } from '../../engine';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';
import {
  JOIN_REQUEST_COOLING_OFF_DAYS,
  approveJoinRequest,
  listJoinRequests,
  rejectJoinRequest,
  reopenJoinRequest,
  type JoinRequest,
  type ReapplicationPolicy,
} from '../../lib/api/joinRequests';

// ═══════════════════════════════════════════════════════════════════
// «SØKNADER OM Å BLI MEDLEM» (#710) — the club's end of the journey.
//
// Somebody registered and asked; this is where a human answers. It renders
// NOTHING when the queue is empty, because a club that nobody has asked should
// not have an empty box explaining a feature they are not using.
//
// ── APPROVING IS WHAT CREATES THE MEMBERSHIP ──────────────────────
//
// Not this screen — the server writes the roster row and the decision in one
// transaction. What this screen owes is to not imply anything else: there is no
// optimistic «approved» state, because the thing that matters is the roster
// row, and claiming it before the server confirms is how a club comes to
// believe somebody is a member who cannot sign in.
//
// ── REJECTING SAYS WHAT IT MEANS ──────────────────────────────────
//
// The API refuses a rejection with no reapplication policy, so every rejection
// records what the club chose. WHAT THE DIALOG OFFERS FIRST is the owner's
// decision of 2026-11-02 (#729): «they may ask again after 30 days», with the
// date filled in, shown, and changeable — the club confirms a visible answer,
// and can pick another date, «any time» or «never» instead.
//
// The three options are written as what they MEAN to the person who asked,
// rather than as the field names: «de kan søke igjen» rather than «allowed».
//
// ── «ASKED BEFORE», AND UNDOING A REJECTION (#729) ────────────────
//
// A request whose person has asked this club before carries a small note. And
// a rejection is not final: «Vis avslåtte søknader» lists the club's rejected
// requests, each of which can be approved directly («Godkjenn likevel») or put
// back in the queue («Åpne igjen»). Collapsed by default — it is the exception,
// and the queue is what the panel is for.
//
// ── THE LIST IS RE-READ AFTER EVERY DECISION ──────────────────────
//
// Rather than removing the row locally. Two operators share this queue — that
// is the ordinary case in a club — and a local removal makes each of them see a
// different list, which is exactly the state that produces two people deciding
// the same request. `already_decided` is therefore an ordinary outcome here,
// not an error: somebody else got there first, and the refresh shows it.
// ═══════════════════════════════════════════════════════════════════

type Decision = 'approve' | 'reject' | 'reopen';
type Busy = { id: string; what: Decision } | null;

export interface JoinRequestsPanelProps {
  /**
   * Whether the API is the backend. Off it there is no queue to read — the
   * legacy backend has no join requests — so the panel does not render at all.
   */
  enabled: boolean;
  /** Told after a decision, so the member list beside it can re-read. */
  onDecided?: () => void;
  /**
   * Open the club's ordinary player card, by the player's (legacy) id.
   *
   * Used only when `hasPlayerCard` says the club's list holds that player.
   * Since #1067 that list is the roster and the club's own events' players, so
   * an APPLICANT is usually not in it — and a card keyed on a list the person
   * is not in opens nothing. For everybody else the name opens the panel's own
   * applicant card below, so the name is always a button that does something.
   */
  onOpenPlayer?: (playerId: string) => void;
  /** Whether the club's player list holds this player, so the full card can open. */
  hasPlayerCard?: (playerId: string) => boolean;
}

export function JoinRequestsPanel({
  enabled,
  onDecided,
  onOpenPlayer,
  hasPlayerCard,
}: JoinRequestsPanelProps) {
  const { t, formatDate } = useLocale();
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  /** The club's rejected requests, for the undo (#729). */
  const [rejected, setRejected] = useState<JoinRequest[]>([]);
  const [showRejected, setShowRejected] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [rejecting, setRejecting] = useState<JoinRequest | null>(null);
  /** The applicant whose card is open — the panel's own, see `onOpenPlayer`. */
  const [viewing, setViewing] = useState<JoinRequest | null>(null);

  const openCard = (request: JoinRequest) => {
    if (onOpenPlayer && (hasPlayerCard?.(request.player.id) ?? true)) {
      onOpenPlayer(request.player.id);
      return;
    }
    setViewing(request);
  };

  const refresh = useCallback(async () => {
    // TWO READS. The rejected list exists only for the undo (#729), so its
    // failure hides the undo and nothing else — the queue is judged on its own.
    const [outcome, past] = await Promise.all([
      listJoinRequests('pending'),
      listJoinRequests('rejected'),
    ]);
    setLoaded(true);
    setRejected(past.status === 'ok' ? past.requests : []);
    if (outcome.status === 'ok') {
      setRequests(outcome.requests);
      setError(null);
      return;
    }
    // A CLUB WITHOUT `member.manage` SEES NOTHING, not an error: the queue is
    // not theirs to act on, and an error implies something is broken.
    //
    // EVERYTHING ELSE SHOWS. A malformed response must not look like «nobody
    // has asked» (#735 review) — the panel renders whenever there is an error,
    // precisely so a broken queue is visible rather than empty.
    setRequests([]);
    if (outcome.status === 'refused') {
      setError(null);
      return;
    }
    setError(outcome.status === 'protocol' ? t('joinQueue.protocol') : t('joinQueue.unreachable'));
  }, [t]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  if (!enabled || !loaded) return null;
  // NOTHING WHEN THERE IS NOTHING. See the header.
  if (requests.length === 0 && rejected.length === 0 && error === null) return null;

  const decide = async (request: JoinRequest, what: Decision, policy?: Policy) => {
    setBusy({ id: request.id, what });
    const outcome =
      what === 'approve'
        ? await approveJoinRequest(request.id)
        : what === 'reopen'
          ? await reopenJoinRequest(request.id)
          : await rejectJoinRequest(request.id, {
              reapplication: policy!.reapplication,
              reapplyAfter: policy!.reapplyAfter,
            });
    setBusy(null);
    setRejecting(null);

    if (outcome.status === 'decided' || outcome.status === 'already_decided') {
      // BOTH REFRESH. «Somebody else answered it» is not a failure here — the
      // queue was stale, and the right response to a stale queue is a fresh one.
      await refresh();
      onDecided?.();
      if (outcome.status === 'already_decided') setError(t('joinQueue.alreadyDecided'));
      return;
    }
    if (outcome.status === 'not_found') {
      await refresh();
      setError(t('joinQueue.gone'));
      return;
    }
    if (outcome.status === 'already_member') {
      // Reopening a rejection of somebody the club has let in since (#729).
      await refresh();
      setError(t('joinQueue.alreadyMember', { name: request.player.name }));
      return;
    }
    if (outcome.status === 'invalid') {
      // ABOUT THE FORM, NOT THE CONNECTION (#735 review). This used to fall
      // into `unreachable`, so a cooling-off date the API refuses as already
      // past was reported as «could not reach the server» — sending somebody to
      // check their wifi over a problem in the field in front of them.
      //
      // The server sends a CODE; this is where it becomes a sentence (#310a).
      setError(
        outcome.reason === 'reapply_after_past'
          ? t('joinQueue.datePast')
          : t('joinQueue.policyRefused'),
      );
      return;
    }
    setError(outcome.status === 'refused' ? t('joinQueue.notAllowed') : t('joinQueue.unreachable'));
  };

  return (
    <div className="card p-4 mb-5">
      <div className="flex items-center gap-2 mb-1">
        <UserCheck className="w-4 h-4 text-ink-3" aria-hidden="true" />
        <h2 className="font-display text-base font-semibold">
          {t('joinQueue.title', { count: requests.length })}
        </h2>
      </div>
      <p className="text-xs text-ink-3 mb-3">{t('joinQueue.lead')}</p>

      {error && <p className="text-xs text-danger mb-3">{error}</p>}

      <ul className="divide-y divide-line">
        {requests.map((request) => (
          <li key={request.id} className="py-2 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              {/* THE NAME OPENS A CARD (owner, 2026-10-20): a club deciding
                  about somebody wants to see who they are. The club's full
                  card when the list holds them, the applicant card otherwise. */}
              <button
                type="button"
                className="text-sm font-medium text-left hover:underline focus-ring rounded"
                aria-label={t('joinQueue.openProfile', { name: request.player.name })}
                onClick={() => openCard(request)}
              >
                {request.player.name}
              </button>
              <div className="text-[11px] text-ink-3 flex flex-wrap items-center gap-x-2">
                <span>
                  {t('joinQueue.asked', { date: asDate(request.requestedAt, formatDate) })}
                </span>
                {/* «ASKED BEFORE» (#729): a small note, nothing more — the
                    count is the person's own asks at this club. */}
                {request.askCount > 1 && (
                  <span className="rounded-full bg-bg-soft px-1.5 py-px text-ink-2">
                    {t('joinQueue.askedBefore')}
                  </span>
                )}
              </div>
              {/* HOW TO REACH THEM, as links the browser already knows what
                  to do with. «Ingen kontaktinfo» rather than a blank line, so
                  an applicant with none does not look like a rendering gap. */}
              <div className="text-[11px] text-ink-2 flex flex-wrap gap-x-3">
                {request.player.email && (
                  <a href={`mailto:${request.player.email}`} className="hover:underline">
                    {request.player.email}
                  </a>
                )}
                {request.player.phone && (
                  <a
                    href={`tel:${request.player.phone.replace(/\s+/g, '')}`}
                    className="hover:underline"
                  >
                    {request.player.phone}
                  </a>
                )}
                {!request.player.email && !request.player.phone && (
                  <span>{t('joinQueue.noContact')}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={busy !== null}
                onClick={() => void decide(request, 'approve')}
              >
                <Check size={12} />
                {busy?.id === request.id && busy.what === 'approve'
                  ? t('joinQueue.approving')
                  : t('joinQueue.approve')}
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                disabled={busy !== null}
                onClick={() => setRejecting(request)}
              >
                <X size={12} />
                {t('joinQueue.reject')}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {rejected.length > 0 && (
        <div className="mt-3 pt-3 border-t border-line">
          <button
            type="button"
            className="text-xs text-ink-2 hover:underline focus-ring rounded"
            aria-expanded={showRejected}
            onClick={() => setShowRejected((open) => !open)}
          >
            {showRejected
              ? t('joinQueue.rejected.hide')
              : t('joinQueue.rejected.show', { count: rejected.length })}
          </button>
          {showRejected && (
            <ul className="divide-y divide-line mt-2">
              {rejected.map((request) => (
                <li
                  key={request.id}
                  className="py-2 flex items-center justify-between gap-3 flex-wrap"
                >
                  <div className="min-w-0">
                    <button
                      type="button"
                      className="text-sm font-medium text-left hover:underline focus-ring rounded"
                      aria-label={t('joinQueue.openProfile', { name: request.player.name })}
                      onClick={() => openCard(request)}
                    >
                      {request.player.name}
                    </button>
                    <div className="text-[11px] text-ink-3 flex flex-wrap gap-x-2">
                      {request.decidedAt && (
                        <span>
                          {t('joinQueue.rejected.on', {
                            date: asDate(request.decidedAt, formatDate),
                          })}
                        </span>
                      )}
                      <span>{policyLine(request, t, formatDate)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="btn-ghost text-xs"
                      disabled={busy !== null}
                      onClick={() => void decide(request, 'reopen')}
                    >
                      <RotateCcw size={12} />
                      {busy?.id === request.id && busy.what === 'reopen'
                        ? t('joinQueue.rejected.reopening')
                        : t('joinQueue.rejected.reopen')}
                    </button>
                    <button
                      type="button"
                      className="btn-primary text-xs"
                      disabled={busy !== null}
                      onClick={() => void decide(request, 'approve')}
                    >
                      <Check size={12} />
                      {busy?.id === request.id && busy.what === 'approve'
                        ? t('joinQueue.approving')
                        : t('joinQueue.rejected.approve')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {rejecting && (
        <RejectDialog
          request={rejecting}
          busy={busy?.what === 'reject'}
          onCancel={() => setRejecting(null)}
          onConfirm={(policy) => void decide(rejecting, 'reject', policy)}
        />
      )}

      {viewing && <ApplicantCard request={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/**
 * Who the applicant is, from what the queue carries.
 *
 * NOT THE CLUB'S PLAYER CARD. That card reads the club's own player list,
 * which since #1067 holds the roster and the club's own events' players — an
 * applicant is usually in neither, and until this card existed the name in
 * the queue opened nothing for them. What a club may see of any player is the
 * sporting projection (#1067): name, level and ELO. Plus the contact details
 * the owner decided the queue shows (2026-10-20), and when they asked.
 */
function ApplicantCard({ request, onClose }: { request: JoinRequest; onClose: () => void }) {
  const { t, formatDate } = useLocale();
  const uid = safeId('applicant-card', useId());
  const titleId = `${uid}-title`;
  const { player } = request;
  const level = player.elo === null ? null : getEloLevel(player.elo);

  return (
    <Dialog
      labelledBy={titleId}
      onClose={onClose}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      className="card p-5 w-full max-w-sm max-h-[92vh] overflow-y-auto"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
    >
      <h2 id={titleId} className="font-display text-base font-semibold mb-1">
        {player.name}
      </h2>
      <p className="text-xs text-ink-3 mb-3">
        {t('joinQueue.asked', { date: asDate(request.requestedAt, formatDate) })}
      </p>

      <dl className="text-sm space-y-2 mb-3">
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {t('joinQueue.card.ratingLabel')}
          </dt>
          <dd>
            {player.elo === null || !level
              ? t('joinQueue.card.noRating')
              : t('joinQueue.card.rating', {
                  level: level.level,
                  name: level.name,
                  // As text: a rating is «1480» everywhere in the portal,
                  // never a grouped number («1 480»).
                  elo: String(Math.round(player.elo)),
                })}
          </dd>
        </div>
        <div>
          <dt className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
            {t('joinQueue.card.contactLabel')}
          </dt>
          <dd className="flex flex-wrap gap-x-3">
            {player.email && (
              <a href={`mailto:${player.email}`} className="hover:underline">
                {player.email}
              </a>
            )}
            {player.phone && (
              <a href={`tel:${player.phone.replace(/\s+/g, '')}`} className="hover:underline">
                {player.phone}
              </a>
            )}
            {!player.email && !player.phone && <span>{t('joinQueue.noContact')}</span>}
          </dd>
        </div>
      </dl>

      <p className="text-xs text-ink-3">{t('joinQueue.card.notInList')}</p>
    </Dialog>
  );
}

interface Policy {
  reapplication: ReapplicationPolicy;
  reapplyAfter?: string;
}

/**
 * What a rejection said about asking again, as a sentence for the club.
 * Display only — the policy itself is the server's record.
 */
function policyLine(
  request: JoinRequest,
  t: ReturnType<typeof useLocale>['t'],
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string,
): string {
  switch (request.reapplication) {
    case 'allowed':
      return t('joinQueue.rejected.policyAllowed');
    case 'blocked':
      return t('joinQueue.rejected.policyBlocked');
    case 'after':
      return request.reapplyAfter
        ? t('joinQueue.rejected.policyAfter', { date: asDate(request.reapplyAfter, formatDate) })
        : '';
    default:
      return '';
  }
}

/**
 * Rejecting, and saying what it means for the future.
 *
 * THE OWNER'S DEFAULT IS PRE-SELECTED (#729, 2026-11-02): «they may ask again
 * from a date», with the date 30 days ahead filled in. It is on screen, so the
 * club confirms an answer it can see, and every other answer is one click
 * away. The button still refuses an «after» with no date, or a date the API
 * would refuse — clearing the field is how the club says «not that date».
 */
function RejectDialog({
  request,
  busy,
  onCancel,
  onConfirm,
}: {
  request: JoinRequest;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (policy: Policy) => void;
}) {
  const t = useT();
  const [choice, setChoice] = useState<ReapplicationPolicy | null>('after');
  const [date, setDate] = useState(() => utcDayFromToday(JOIN_REQUEST_COOLING_OFF_DAYS));

  // ── THE EARLIEST DATE THE API WILL ACCEPT (#735 review) ──────────
  //
  // It refuses `reapplyAfter <= now`, and the value sent is midnight UTC of the
  // chosen day — so TODAY is already past the moment the day begins, and was
  // being offered anyway. The server then answered 400 and the panel said «could
  // not reach the server», which sent somebody to check their connection over a
  // problem in the form in front of them.
  //
  // So tomorrow is the floor, the input carries it as `min`, and the button
  // checks it too: `min` is advisory in several browsers and absent in some.
  const earliest = tomorrowUtc();
  const chosenIsFuture = date !== '' && date >= earliest;
  const ready = choice !== null && (choice !== 'after' || chosenIsFuture);
  const uid = safeId('reject-join', useId());
  const ids = { title: `${uid}-title`, lead: `${uid}-lead` };

  // A `Dialog` (#900). The backdrop does not close it, as before: it is a
  // decision with three answers. Escape means «Avbryt» — except while the
  // rejection is being sent, when Avbryt is disabled too.
  return (
    <Dialog
      labelledBy={ids.title}
      onClose={busy ? () => undefined : onCancel}
      showClose={false}
      closeOnBackdropClick={false}
      backdropClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      className="card p-5 w-full max-w-sm max-h-[92vh] overflow-y-auto"
      style={{ maxHeight: 'calc(100dvh - 2rem)' }}
    >
      <h2 id={ids.title} className="font-display text-base font-semibold mb-1">
        {t('joinQueue.rejectTitle', { name: request.player.name })}
      </h2>
      <p id={ids.lead} className="text-xs text-ink-3 mb-3">
        {t('joinQueue.rejectLead', { days: String(JOIN_REQUEST_COOLING_OFF_DAYS) })}
      </p>

      {/* The lead line IS the question these three radios answer, so it
            names their group. */}
      <div className="space-y-2 mb-3" role="radiogroup" aria-labelledby={ids.lead}>
        {(['allowed', 'after', 'blocked'] as const).map((option) => (
          <label key={option} className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="radio"
              name="reapplication"
              checked={choice === option}
              onChange={() => setChoice(option)}
              className="mt-1"
            />
            <span>{t(`joinQueue.policy.${option}` as 'joinQueue.policy.allowed')}</span>
          </label>
        ))}
      </div>

      {choice === 'after' && (
        <label className="block mb-3">
          <span className="block text-xs font-mono uppercase tracking-wider text-ink-3 mb-1.5">
            {t('joinQueue.fromDate')}
          </span>
          <input
            type="date"
            value={date}
            min={earliest}
            onChange={(e) => setDate(e.target.value)}
            className="input w-full"
          />
        </label>
      )}
      {/* OUTSIDE THE LABEL, deliberately. Inside, it becomes part of the
            field's accessible name — a screen reader would announce «Kan søke
            igjen fra Velg en dato fram i tid …» as the label itself, and the
            message stops being an error and becomes a title. A test looking the
            field up by its label is what found it. */}
      {choice === 'after' && date !== '' && !chosenIsFuture && (
        <p role="alert" className="text-xs text-danger mb-3">
          {t('joinQueue.datePast')}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={!ready || busy}
          onClick={() =>
            onConfirm({
              reapplication: choice!,
              // MIDNIGHT UTC on the chosen day. The API refuses a date that is
              // already past, and a bare `YYYY-MM-DD` is unambiguous — building
              // a local-time instant here would make «1. mars» mean a
              // different moment depending on who clicked it.
              reapplyAfter: choice === 'after' ? `${date}T00:00:00.000Z` : undefined,
            })
          }
        >
          {busy ? t('joinQueue.rejecting') : t('joinQueue.confirmReject')}
        </button>
        <button type="button" className="btn-ghost" disabled={busy} onClick={onCancel}>
          {t('joinQueue.cancel')}
        </button>
      </div>
    </Dialog>
  );
}

/**
 * The first day whose midnight UTC is still in the future — tomorrow.
 *
 * THE API REFUSES `reapplyAfter <= now`, and the value sent is midnight UTC of
 * the chosen day, so TODAY is already past the moment the day begins (#735
 * review). Computed in UTC because that is what is SENT: deriving it from a
 * local calendar would be a day out for anybody west of Greenwich, and the
 * mistake would only show up in the evening.
 */
function tomorrowUtc(now: Date = new Date()): string {
  return utcDayFromToday(1, now);
}

/**
 * The `YYYY-MM-DD` `days` after today, in UTC — the unit the date input speaks
 * and the day whose midnight is sent. A wire value for the API, never a
 * storage key (§4); the default cooling-off date is built with it (#729).
 */
function utcDayFromToday(days: number, now: Date = new Date()): string {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + days));
  return next.toISOString().slice(0, 10);
}

/**
 * A date for a human. Display only — never a key, never compared (§4).
 *
 * THROUGH THE LOCALE, not `toLocaleDateString()`. The bare call uses the
 * RUNTIME's default locale, which rendered «10/1/2026» in a Norwegian app — a
 * date that reads as 10 January to the people using it and means 1 October.
 * Caught by looking at the preview; no test would have minded.
 */
function asDate(
  iso: string,
  formatDate: (value: Date | number, options?: Intl.DateTimeFormatOptions) => string,
): string {
  const parsed = new Date(iso);
  if (!Number.isFinite(parsed.getTime())) return iso;
  return formatDate(parsed, { day: 'numeric', month: 'short', year: 'numeric' });
}
